import React, { useState } from "react";
import { MEDICINE_CARDS, RARITY } from "../data/medicineCardData";

const FILTERS = ["All", "Common", "Uncommon", "Rare", "Legendary"];

// Per-rarity card design tokens
const CARD_STYLE = {
  common: {
    headerGrad: "linear-gradient(135deg, #9ca3af 0%, #6b7280 100%)",
    artGrad:    "linear-gradient(160deg, #f3f4f6 0%, #e5e7eb 100%)",
    stars:      "●",
    starColor:  "#9ca3af",
    foil:       false,
  },
  uncommon: {
    headerGrad: "linear-gradient(135deg, #60a5fa 0%, #2563eb 100%)",
    artGrad:    "linear-gradient(160deg, #dbeafe 0%, #bfdbfe 100%)",
    stars:      "●●",
    starColor:  "#3b82f6",
    foil:       false,
  },
  rare: {
    headerGrad: "linear-gradient(135deg, #a78bfa 0%, #7c3aed 100%)",
    artGrad:    "linear-gradient(160deg, #ede9fe 0%, #c4b5fd 100%)",
    stars:      "★★★",
    starColor:  "#8b5cf6",
    foil:       true,
  },
  legendary: {
    headerGrad: "linear-gradient(135deg, #f59e0b 0%, #f97316 50%, #ef4444 100%)",
    artGrad:    "linear-gradient(160deg, #fef9c3 0%, #fde68a 50%, #fcd34d 100%)",
    stars:      "✦✦✦✦",
    starColor:  "#f59e0b",
    foil:       true,
  },
};

// Injected keyframes for the holo shimmer
const HOLO_STYLE = `
  @keyframes holoShift {
    0%   { background-position: 0% 50%; }
    50%  { background-position: 100% 50%; }
    100% { background-position: 0% 50%; }
  }
  @keyframes legendaryShift {
    0%   { background-position: 0% 50%; opacity: 0.45; }
    50%  { background-position: 100% 50%; opacity: 0.65; }
    100% { background-position: 0% 50%; opacity: 0.45; }
  }
`;

const FoilOverlay = ({ rarity }) => {
  if (rarity === "legendary") {
    return (
      <div style={{
        position: "absolute", inset: 0, borderRadius: "inherit", pointerEvents: "none",
        background: "linear-gradient(135deg, rgba(255,215,0,0.3) 0%, rgba(255,100,100,0.25) 20%, rgba(100,200,255,0.25) 40%, rgba(150,100,255,0.25) 60%, rgba(255,200,50,0.3) 80%, rgba(255,215,0,0.3) 100%)",
        backgroundSize: "300% 300%",
        animation: "legendaryShift 3s ease infinite",
        mixBlendMode: "overlay",
      }} />
    );
  }
  if (rarity === "rare") {
    return (
      <div style={{
        position: "absolute", inset: 0, borderRadius: "inherit", pointerEvents: "none",
        background: "linear-gradient(135deg, rgba(167,139,250,0.2) 0%, rgba(196,181,253,0.35) 50%, rgba(167,139,250,0.2) 100%)",
        backgroundSize: "200% 200%",
        animation: "holoShift 2.5s ease infinite",
        mixBlendMode: "overlay",
      }} />
    );
  }
  return null;
};

// ── Small card tile shown in the grid ────────────────────────────────────────
const CardTile = ({ card, owned, selected, onClick }) => {
  const r  = RARITY[card.rarity];
  const cs = CARD_STYLE[card.rarity];

  if (!owned) {
    return (
      <div style={{
        borderRadius: 12, border: "2.5px solid #374151",
        background: "#1f2937", overflow: "hidden", opacity: 0.5,
        boxShadow: "0 2px 6px rgba(0,0,0,0.3)",
      }}>
        <div style={{ height: 36, background: "#374151" }} />
        <div style={{
          display: "flex", flexDirection: "column", alignItems: "center",
          justifyContent: "center", padding: "10px 6px 6px", gap: 4,
        }}>
          <div style={{ fontSize: 22 }}>🔒</div>
          <div style={{ fontSize: 9, color: "#6b7280", fontWeight: 700 }}>???</div>
          <div style={{
            fontSize: 8, color: "#6b7280", background: "#374151",
            borderRadius: 4, padding: "1px 5px", fontWeight: 600,
          }}>{r.label}</div>
        </div>
      </div>
    );
  }

  return (
    <button
      onClick={onClick}
      style={{
        borderRadius: 12,
        border: `2.5px solid ${selected ? r.color : r.border}`,
        background: "white",
        overflow: "hidden",
        boxShadow: selected
          ? `0 0 18px ${r.glow}, 0 4px 12px rgba(0,0,0,0.25)`
          : "0 2px 8px rgba(0,0,0,0.15)",
        transform: selected ? "scale(1.05)" : "scale(1)",
        transition: "all 0.2s ease",
        position: "relative",
        textAlign: "left",
        cursor: "pointer",
        width: "100%",
      }}
    >
      <style>{HOLO_STYLE}</style>

      {/* Name bar */}
      <div style={{
        background: cs.headerGrad, padding: "4px 7px",
        display: "flex", justifyContent: "space-between", alignItems: "center",
      }}>
        <span style={{
          color: "white", fontWeight: 800, fontSize: 9,
          textShadow: "0 1px 3px rgba(0,0,0,0.4)", lineHeight: 1.2,
          maxWidth: "75%", overflow: "hidden",
        }}>{card.name}</span>
        <span style={{ color: "rgba(255,255,255,0.85)", fontSize: 8, fontWeight: 700 }}>
          HP {card.rarity === "legendary" ? 120 : card.rarity === "rare" ? 100 : card.rarity === "uncommon" ? 80 : 60}
        </span>
      </div>

      {/* Artwork area */}
      <div style={{
        background: cs.artGrad, position: "relative",
        display: "flex", alignItems: "center", justifyContent: "center",
        padding: card.image ? 0 : "10px 6px",
        minHeight: 64, overflow: "hidden",
      }}>
        <FoilOverlay rarity={card.rarity} />
        {card.image ? (
          <img src={card.image} alt={card.name} style={{ width: "100%", height: 80, objectFit: "cover", display: "block" }} />
        ) : (
          <span style={{ fontSize: 34, lineHeight: 1, position: "relative", zIndex: 1 }}>{card.emoji}</span>
        )}
      </div>

      {/* Card body */}
      <div style={{ padding: "5px 7px 7px", background: "white" }}>
        <div style={{
          fontSize: 8, fontWeight: 700, color: r.color,
          background: r.bg, borderRadius: 4, padding: "1px 5px",
          display: "inline-block", border: `1px solid ${r.border}`,
          marginBottom: 4,
        }}>{card.category}</div>
        <div style={{
          display: "flex", justifyContent: "flex-end",
          fontSize: 10, color: cs.starColor, letterSpacing: 1,
        }}>{cs.stars}</div>
      </div>

      {/* Foil border shimmer for rare+ */}
      {cs.foil && (
        <div style={{
          position: "absolute", inset: 0, borderRadius: 10, pointerEvents: "none",
          border: `2px solid transparent`,
          background: card.rarity === "legendary"
            ? "transparent padding-box, linear-gradient(135deg,#f59e0b,#f97316,#ef4444,#f59e0b) border-box"
            : "transparent padding-box, linear-gradient(135deg,#a78bfa,#c4b5fd,#7c3aed,#a78bfa) border-box",
        }} />
      )}
    </button>
  );
};

// ── Full-size card shown in the modal ────────────────────────────────────────
const FullCard = ({ card, onClose }) => {
  const r  = RARITY[card.rarity];
  const cs = CARD_STYLE[card.rarity];
  const cardIndex = MEDICINE_CARDS.findIndex(c => c.id === card.id) + 1;

  return (
    <div
      onClick={onClose}
      style={{
        position: "fixed", inset: 0, zIndex: 50,
        background: "rgba(0,0,0,0.75)", backdropFilter: "blur(4px)",
        display: "flex", alignItems: "center", justifyContent: "center",
        padding: 24,
      }}
    >
      <div
        onClick={e => e.stopPropagation()}
        style={{
          width: "100%", maxWidth: 300,
          borderRadius: 20,
          border: `4px solid ${r.border}`,
          background: "white",
          boxShadow: `0 0 40px ${r.glow}, 0 20px 60px rgba(0,0,0,0.5)`,
          overflow: "hidden",
          position: "relative",
        }}
      >
        <style>{HOLO_STYLE}</style>

        {/* Name + HP bar */}
        <div style={{
          background: cs.headerGrad, padding: "10px 14px",
          display: "flex", justifyContent: "space-between", alignItems: "center",
        }}>
          <span style={{
            color: "white", fontWeight: 900, fontSize: 16,
            textShadow: "0 1px 4px rgba(0,0,0,0.4)", letterSpacing: 0.3,
          }}>{card.name}</span>
          <div style={{ textAlign: "right" }}>
            <div style={{ color: "rgba(255,255,255,0.7)", fontSize: 9, fontWeight: 600 }}>HP</div>
            <div style={{ color: "white", fontWeight: 900, fontSize: 18, lineHeight: 1 }}>
              {card.rarity === "legendary" ? 120 : card.rarity === "rare" ? 100 : card.rarity === "uncommon" ? 80 : 60}
            </div>
          </div>
        </div>

        {/* Artwork */}
        <div style={{
          background: cs.artGrad, position: "relative",
          display: "flex", alignItems: "center", justifyContent: "center",
          padding: card.image ? 0 : "20px 16px",
          borderBottom: `2px solid ${r.border}`,
          overflow: "hidden", minHeight: card.image ? 160 : "auto",
        }}>
          <FoilOverlay rarity={card.rarity} />
          {card.image ? (
            <img src={card.image} alt={card.name} style={{ width: "100%", height: 160, objectFit: "cover", display: "block" }} />
          ) : (
            <span style={{ fontSize: 80, lineHeight: 1, position: "relative", zIndex: 1, filter: "drop-shadow(0 4px 12px rgba(0,0,0,0.2))" }}>
              {card.emoji}
            </span>
          )}
          {/* Category type chip — bottom right of artwork */}
          <div style={{
            position: "absolute", bottom: 10, right: 12,
            background: cs.headerGrad, color: "white",
            fontSize: 10, fontWeight: 800, padding: "3px 10px",
            borderRadius: 20, boxShadow: "0 2px 6px rgba(0,0,0,0.25)",
            textTransform: "uppercase", letterSpacing: 0.5,
          }}>{card.category}</div>
        </div>

        {/* Card body */}
        <div style={{ padding: "12px 14px 10px", background: "white" }}>
          {/* Ability / tagline */}
          <div style={{
            borderRadius: 8, border: `1.5px solid ${r.border}`,
            background: r.bg, padding: "8px 10px", marginBottom: 10,
          }}>
            <div style={{
              fontSize: 9, fontWeight: 800, color: r.color,
              textTransform: "uppercase", letterSpacing: 1, marginBottom: 4,
            }}>✦ Key Fact</div>
            <p style={{ fontSize: 11, color: "#374151", lineHeight: 1.5, margin: 0 }}>{card.fact}</p>
          </div>

          {/* Tagline as flavour text */}
          <p style={{
            fontSize: 10, fontStyle: "italic", color: "#9ca3af",
            borderTop: "1px solid #e5e7eb", paddingTop: 8, margin: 0,
            textAlign: "center",
          }}>"{card.tagline}"</p>
        </div>

        {/* Footer — rarity stars + card number */}
        <div style={{
          background: r.bg, borderTop: `1.5px solid ${r.border}`,
          padding: "6px 14px",
          display: "flex", justifyContent: "space-between", alignItems: "center",
        }}>
          <span style={{ fontSize: 12, color: cs.starColor, letterSpacing: 2 }}>{cs.stars}</span>
          <span style={{ fontSize: 9, color: "#9ca3af", fontWeight: 600 }}>
            {String(cardIndex).padStart(3, "0")}/{String(MEDICINE_CARDS.length).padStart(3, "0")}
          </span>
          <span style={{
            fontSize: 9, fontWeight: 800, color: r.color,
            textTransform: "uppercase", letterSpacing: 0.5,
          }}>{r.label}</span>
        </div>

        {/* Foil border for rare+ */}
        {cs.foil && (
          <div style={{
            position: "absolute", inset: 0, borderRadius: 16, pointerEvents: "none",
            border: "3px solid transparent",
            background: card.rarity === "legendary"
              ? "transparent padding-box, linear-gradient(135deg,#f59e0b,#f97316,#ef4444,#a78bfa,#60a5fa,#f59e0b) border-box"
              : "transparent padding-box, linear-gradient(135deg,#a78bfa,#60a5fa,#c4b5fd,#7c3aed,#a78bfa) border-box",
          }} />
        )}

        <button
          onClick={onClose}
          style={{
            position: "absolute", top: 10, right: 10,
            background: "rgba(0,0,0,0.35)", border: "none",
            borderRadius: "50%", width: 26, height: 26,
            color: "white", fontSize: 14, cursor: "pointer",
            display: "flex", alignItems: "center", justifyContent: "center",
            zIndex: 10,
          }}
        >✕</button>
      </div>
    </div>
  );
};

// ── Main screen ──────────────────────────────────────────────────────────────
const MedicineCardCollection = ({ medicineCards = [], onClose }) => {
  const [filter, setFilter]   = useState("All");
  const [selected, setSelected] = useState(null);

  const owned      = new Set(medicineCards);
  const ownedCount = MEDICINE_CARDS.filter(c => owned.has(c.id)).length;
  const total      = MEDICINE_CARDS.length;

  const visible = MEDICINE_CARDS.filter(c =>
    filter === "All" || c.rarity === filter.toLowerCase()
  );

  const handleSelect = (card) => {
    if (!owned.has(card.id)) return;
    setSelected(card);
  };

  return (
    <div className="min-h-screen bg-gray-950 flex flex-col">
      <style>{HOLO_STYLE}</style>

      {/* Header */}
      <div className="bg-gray-900 px-4 py-3 flex items-center justify-between flex-shrink-0">
        <button onClick={onClose} className="text-sm text-gray-300 hover:text-white">← Back</button>
        <span className="font-bold text-white text-sm">💊 Medicine Cards</span>
        <span className="text-xs text-gray-400">{ownedCount}/{total}</span>
      </div>

      {/* Progress bar */}
      <div className="px-4 pt-3 pb-2 bg-gray-900 flex-shrink-0">
        <div className="h-2 bg-gray-700 rounded-full overflow-hidden">
          <div
            className="h-full rounded-full transition-all duration-500"
            style={{
              width: `${total > 0 ? (ownedCount / total) * 100 : 0}%`,
              background: "linear-gradient(to right, #f59e0b, #f97316)",
            }}
          />
        </div>
        <p className="text-xs text-gray-400 mt-1.5 text-center">
          {ownedCount === 0 && "Win battles to collect medicine cards"}
          {ownedCount > 0 && ownedCount < total && `${ownedCount} of ${total} cards collected`}
          {ownedCount === total && "🎉 Full collection!"}
        </p>
      </div>

      {/* Filter tabs */}
      <div className="flex gap-1.5 px-4 py-2 bg-gray-900 flex-shrink-0 overflow-x-auto">
        {FILTERS.map(f => {
          const active = filter === f;
          const r = f !== "All" ? RARITY[f.toLowerCase()] : null;
          return (
            <button
              key={f}
              onClick={() => { setFilter(f); setSelected(null); }}
              className="px-3 py-1 rounded-full text-xs font-semibold whitespace-nowrap transition-all flex-shrink-0"
              style={
                active && r
                  ? { background: r.bg, color: r.color, border: `1.5px solid ${r.border}` }
                  : active
                  ? { background: "#374151", color: "#f9fafb", border: "1.5px solid #6b7280" }
                  : { background: "#1f2937", color: "#9ca3af", border: "1.5px solid #374151" }
              }
            >
              {f}
            </button>
          );
        })}
      </div>

      {/* Card grid — 2 columns so cards are big enough to read */}
      <div className="flex-1 overflow-y-auto p-4">
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4">
          {visible.map(card => (
            <CardTile
              key={card.id}
              card={card}
              owned={owned.has(card.id)}
              selected={selected?.id === card.id}
              onClick={() => handleSelect(card)}
            />
          ))}
        </div>
      </div>

      {/* Full-card modal */}
      {selected && <FullCard card={selected} onClose={() => setSelected(null)} />}
    </div>
  );
};

export default MedicineCardCollection;
